/**
 * Single source of truth for top-bar module entries.
 *
 * A new module requires THREE edits:
 *   1) @ams/shared/src/module-access.ts — extend ModuleId + add predicate
 *   2) this file — add a MODULES entry
 *   3) @ams/shared/src/admin-privileges.ts — add the module's privileges so
 *      the Admin role-edit UI can assign them (otherwise only seeded
 *      superadmin with `*` can access the module)
 *
 * The `icon` field is a string (emoji for now). Swap to an icon-component
 * type when the design system justifies it; no predicate-map changes needed.
 */

import type { ModuleId } from '@ams/shared/module-access';

export interface ModuleDef {
  id: ModuleId;
  label: string;
  icon: string;
  path: string;
  /**
   * When true the nav entry is a plain `<a href>` (full-page navigation),
   * not a react-router `<NavLink>` — and `ResolveHome` redirects to it via
   * `window.location`. Used for modules that are a separate Vite entry /
   * standalone site rather than a route in this SPA.
   */
  external?: boolean;
}

// Order controls two things:
//   1. Nav-bar rendering left-to-right on `AppShell`.
//   2. `ResolveHome` fallback — iterates MODULES in order and redirects to
//      the first accessible (skipping `settings`). Put the module most users
//      should land on first at the top.
// User preference (2026-04-19): Test Data before ExpoAMS for the Gen-3
// technician / data-analyst persona.
export const MODULES: ModuleDef[] = [
  { id: 'stationDataDashboard', label: 'Test Data', icon: '📈', path: '/station-data-dashboard' },
  { id: 'expoams', label: 'ExpoAMS', icon: '📦', path: '/expoams' },
  { id: 'bom', label: 'BOM', icon: '🧱', path: '/bom' },
  { id: 'inventory', label: 'Inventory', icon: '📋', path: '/inventory' },
  {
    id: 'commercialConsole',
    label: 'Commercial',
    icon: '💼',
    path: '/commercial-console',
    external: true,
  },
  { id: 'email', label: 'Email', icon: '📧', path: '/email' },
  { id: 'conference', label: 'Conference', icon: '🎪', path: '/conference' },
  { id: 'procurement', label: 'Procurement', icon: '📥', path: '/procurement' },
  { id: 'settings', label: 'Settings', icon: '⚙️', path: '/settings' },
  { id: 'admin', label: 'Admin', icon: '🛠️', path: '/admin' },
];
